import { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import MaskedField from "../Fields/MaskedField";

import { lang } from "../../../i18nLang";

const InputField = (props) => {
  const {
    initialValue,
    required,
    mask,
    prefix,
    suffix,
    value,
    onChange,
    ...otherProps
  } = props;
  return mask ? (
    <MaskedField
      prefix={prefix}
      suffix={suffix}
      value={value}
      onChange={onChange}
      {...otherProps}
    />
  ) : (
    <TextField value={value} onChange={onChange} {...otherProps} />
  );
};

type IField = {
  name: string;
  label: string;
  initialValue?: any;
  type?: string; // inputs type
  required?: boolean;
  mask?: boolean;
  prefix?: string;
  suffix?: string;
};

type IProps = {
  title: string;
  open: boolean;
  onClose: Function;
  onConfirm?: Function;
  cancelLabel: string;
  confirmLabel: string;
  fields: Array<IField>;
};

/**
 * Componente Dialog com Inputs.
 * @category Components
 */
function DialogModalInputs({
  title,
  open,
  onClose,
  onConfirm,
  cancelLabel,
  confirmLabel,
  fields,
}: IProps) {
  const [data, setData] = useState(createInitialData(fields));

  const handleDataChange = (ev) => {
    const { name, value } = ev.target;

    if (name && value !== undefined) {
      const newData = data ? { ...data } : {};
      newData[name] = value;

      setData(newData);
      ev.target.value = value;
    }
  };

  const verifyRequireds = (): boolean =>
    !fields.length ||
    fields.some((field) => field.required && !data[field.name]);

  const handleClickClose = () => onClose();

  const handleClickConfirm = async () => {
    if (onConfirm) {
      const error = await onConfirm(data);
      if (error) return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClickClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <DialogContent>
        {fields.map(({ name, ...field }, index) => (
          <InputField
            {...field}
            key={name}
            name={name}
            value={data[name]}
            onChange={handleDataChange}
            autoFocus={index === 0}
            margin="dense"
            onKeyDown={(ev) => ev.stopPropagation()}
            fullWidth
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button color="default" onClick={handleClickClose}>
          {cancelLabel}
        </Button>
        <Button
          color="primary"
          onClick={handleClickConfirm}
          disabled={verifyRequireds()}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DialogModalInputs.propTypes = {
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  cancelLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      initialValue: PropTypes.any,
      type: PropTypes.string, // inputs type
      required: PropTypes.bool,
      mask: PropTypes.bool,
      prefix: PropTypes.string,
      suffix: PropTypes.string,
    })
  ).isRequired,
};

DialogModalInputs.defaultProps = {
  cancelLabel: lang("cancel"),
  confirmLabel: "Ok",
};

function createInitialData(fields) {
  let newData = {};
  fields.forEach((field) => (newData[field.name] = field.initialValue || ""));
  return newData;
}

export default DialogModalInputs;
